// src/pages/Profile/hooks/useProfilePage.js

import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import {
  getUserById,
  updateUserProfile,
  toggleEmailNotifications,
} from "../../../services/userServices";

import {
  uploadUserPhoto,
  cropUserPhoto,
  toAbsoluteUserPhotoUrl,
} from "../../../services/userPhotoServices";

import { toMasterUrl } from "../../../components/CropPhotoModal/CropPhotoModal";
import { getRoleFromRoleId } from "../../../utils/roleHelper";

import {
  DEFAULT_AVATAR,
  DEFAULT_ROLE_META,
  INITIAL_USER_STATE,
  ROLE_META,
} from "../data/profileData";

import {
  getDisplayName,
  getStoredUserInfo,
  normalizeUserFromApi,
  updateLocalStorageUser,
  withBust,
} from "../utils/profileUtils";

export default function useProfilePage() {
  const [user, setUser] = useState(INITIAL_USER_STATE);

  const [isEditing, setIsEditing] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  const [uploadMode, setUploadMode] = useState("url");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp");

  const fileInputRef = useRef(null);

  const [imgVersion, setImgVersion] = useState(0);
  const [imgOk, setImgOk] = useState(true);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [isRefreshingRole, setIsRefreshingRole] = useState(false);
  const [loading, setLoading] = useState(true);

  const userRole = getRoleFromRoleId(user?.role_id);

  const roleMeta = ROLE_META[user.role_id] || {
    ...DEFAULT_ROLE_META,
    label: `Rôle inconnu (${user.role_id})`,
  };

  const absolutePhoto = toAbsoluteUserPhotoUrl(user.photoURL);

  const previewSrc = absolutePhoto
    ? withBust(absolutePhoto, imgVersion)
    : withBust(DEFAULT_AVATAR, imgVersion);

  const displayName = useMemo(() => getDisplayName(user), [user]);

  const cropImageUrl = user.photoURL
    ? `${toAbsoluteUserPhotoUrl(toMasterUrl(user.photoURL))}?v=${imgVersion}`
    : "";

  const refreshUserProfile = useCallback(async (showToastMessage = false) => {
    const userInfo = getStoredUserInfo();
    const userId = userInfo?.userId;

    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const userData = await getUserById(userId);

      setUser((previous) => normalizeUserFromApi(previous, userData));

      setImgOk(true);
      setImgVersion((version) => version + 1);

      updateLocalStorageUser({
        nom: userData.nom,
        prenom: userData.prenom,
        photoURL: userData.photoURL,
        role_id: userData.role_id,
        userId: userInfo.userId ?? userData.id,
        push_notifications_enabled: !!userData.push_notifications_enabled,
      });

      if (showToastMessage) {
        toast.success("Profil et rôle actualisés !");
      }
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des données de l'utilisateur:",
        error
      );

      toast.error("Erreur lors de l’actualisation du profil.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCloseModal = () => {
    setIsEditing(false);
    setIsPhotoModalOpen(false);
    setIsUploading(false);
    setUploadProgress(0);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setUser((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleUserUpdated = useCallback((payload = {}) => {
    setUser((previous) => ({
      ...previous,
      ...payload,
    }));

    updateLocalStorageUser(payload);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      const token = localStorage.getItem("token");

      const updated = await updateUserProfile(user.id, user, token);

      updateLocalStorageUser({
        nom: updated.nom,
        prenom: updated.prenom,
        photoURL: updated.photoURL || user.photoURL,
        role_id: updated.role_id ?? user.role_id,
      });

      setUser((previous) => ({
        ...previous,
        ...updated,
      }));

      setIsEditing(false);

      toast.success("Profil mis à jour avec succès !");
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour des données de l'utilisateur:",
        error
      );

      toast.error("Erreur lors de la mise à jour du profil.");
    }
  };

  const openPhotoModal = () => {
    setIsPhotoModalOpen(true);
    setUploadMode("url");
    setUploadProgress(0);
    setIsUploading(false);
    setImgOk(true);
  };

  const openFilePicker = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    event.stopPropagation();

    const file = event.target.files?.[0];

    if (!file) return;

    if (!user?.id) {
      toast.error("ID utilisateur manquant.");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const response = await uploadUserPhoto(user.id, file, {
        format: outputFormat,
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;

          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );

          setUploadProgress(percent);
        },
      });

      const newPhotoUrl =
        response?.photo_url ||
        response?.photoURL ||
        response?.imageUrl ||
        response?.url ||
        response?.path ||
        response?.data?.photo_url ||
        response?.data?.photoURL ||
        response?.data?.imageUrl ||
        response?.data?.url ||
        response?.data?.path;

      if (newPhotoUrl) {
        setUser((previous) => ({
          ...previous,
          photoURL: newPhotoUrl,
        }));

        updateLocalStorageUser({
          photoURL: newPhotoUrl,
        });

        setImgOk(true);
        setImgVersion((version) => version + 1);

        toast.success("Photo uploadée et traitée.");
      } else {
        console.warn("Réponse upload inattendue:", response);
        toast.warn("Upload terminé mais URL non renvoyée.");
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error(error);
      toast.error(`Erreur upload : ${error?.message || "inconnue"}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handlePhotoSave = async (event) => {
    event.preventDefault();

    if (uploadMode === "url") {
      try {
        const token = localStorage.getItem("token");
        const updated = await updateUserProfile(user.id, { ...user }, token);

        const nextPhoto = updated.photoURL || user.photoURL;

        updateLocalStorageUser({
          photoURL: nextPhoto,
        });

        setUser((previous) => ({
          ...previous,
          photoURL: nextPhoto,
        }));

        setImgVersion((version) => version + 1);
        setIsPhotoModalOpen(false);

        toast.success("Photo de profil mise à jour !");
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de l’enregistrement de la photo.");
      }

      return;
    }

    setIsPhotoModalOpen(false);
  };

  const handleToggleEmailNotifications = async () => {
    const newValue = !user.email_notifications_enabled;

    try {
      const token = localStorage.getItem("token");

      const updatedUser = await toggleEmailNotifications(
        user.id,
        newValue,
        token
      );

      setUser((previous) => ({
        ...previous,
        email_notifications_enabled: updatedUser.email_notifications_enabled,
      }));

      toast.success(
        `Notifications par mail ${newValue ? "activées" : "désactivées"} !`
      );
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour de la préférence mail.");
    }
  };

  const handleRefreshRole = async () => {
    try {
      setIsRefreshingRole(true);

      await refreshUserProfile(true);

      window.location.reload();
    } catch (error) {
      console.error(error);
      toast.error("Impossible de rafraîchir le rôle.");
    } finally {
      setIsRefreshingRole(false);
    }
  };

  const handleCropped = (newUrl) => {
    setUser((previous) => ({
      ...previous,
      photoURL: newUrl,
    }));

    updateLocalStorageUser({
      photoURL: newUrl,
    });

    setImgVersion((version) => version + 1);
    toast.success("Photo recadrée.");
  };

  const handlePhotoUrlChange = (value) => {
  setUser((previous) => ({
    ...previous,
    photoURL: value,
  }));

  setImgOk(true);
  setImgVersion((version) => version + 1);
};

  return {
    user,
    setUser,

    loading,
    userRole,
    roleMeta,
    displayName,
    previewSrc,
    imgVersion,
    imgOk,
    setImgOk,

    isEditing,
    setIsEditing,
    isPhotoModalOpen,
    setIsPhotoModalOpen,

    uploadMode,
    setUploadMode,
    isUploading,
    uploadProgress,
    outputFormat,
    setOutputFormat,
    fileInputRef,

    isCropOpen,
    setIsCropOpen,
    cropImageUrl,
    cropUserPhoto,

    isRefreshingRole,

    refreshUserProfile,
    handleCloseModal,
    handleChange,
    handleUserUpdated,
    handleSubmit,
    openPhotoModal,
    openFilePicker,
    handleFileChange,
    handlePhotoSave,
    handleToggleEmailNotifications,
    handleRefreshRole,
    handleCropped,
    handlePhotoUrlChange,
  };
}